/**
 * Simple Slider - Enhanced Slider Class for Shopify Themes
 * Supports autoplay, pause on hover, customizable controls and animations
 */
class SimpleSlider {
  /**
   * Initialize the slider
   * @param {string} selector - CSS selector for the slider container
   * @param {Object} options - Configuration options
   */
  constructor(selector, options = {}) {
    this.sliderContainer = document.querySelector(selector);
    if (!this.sliderContainer) {
      console.error('Slider container not found');
      return;
    }

    // Default configuration
    this.config = {
      duration: options.duration || 5000,
      autoplay: options.autoplay !== undefined ? options.autoplay : true,
      pauseOnHover: options.pauseOnHover !== undefined ? options.pauseOnHover : true,
      showArrows: options.showArrows !== undefined ? options.showArrows : true,
      showDots: options.showDots !== undefined ? options.showDots : true,
      fadeSpeed: options.fadeSpeed || 500, // Fade transition speed in ms
    };

    // Slider state
    this.slides = Array.from(this.sliderContainer.querySelectorAll('.slide'));
    this.currentSlide = 0;
    this.slideCount = this.slides.length;
    this.isPlaying = false;
    this.interval = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    
    // Initialize only if slides exist
    if (this.slideCount > 0) {
      this.init();
    } else {
      console.error('No slides found in slider container');
    }
  }

  /**
   * Initialize the slider
   */
  init() {
    console.log('Initializing slider with ' + this.slideCount + ' slides');
    
    // Always make sure at least the first slide is visible
    if (this.slides.length > 0) {
      this.slides[0].style.opacity = '1';
      this.slides[0].style.visibility = 'visible';
    }
    
    // Create controls
    this.createControls();
    
    // Start autoplay if enabled
    if (this.config.autoplay) {
      this.play();
    }
    
    // Add event listeners
    this.addEventListeners();
    
    // Mark slider as initialized
    this.sliderContainer.classList.add('slider-initialized');
  }

  /**
   * Create navigation controls
   */
  createControls() {
    // Create dots if enabled and multiple slides
    if (this.config.showDots && this.slideCount > 1) {
      const dotsContainer = document.createElement('div');
      dotsContainer.className = 'slider-dots';
      
      for (let i = 0; i < this.slideCount; i++) {
        const dot = document.createElement('button');
        dot.className = i === 0 ? 'slider-dot active' : 'slider-dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.dataset.index = i;
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.goToSlide(i);
        });
        dotsContainer.appendChild(dot);
      }
      
      this.sliderContainer.appendChild(dotsContainer);
      this.dots = Array.from(dotsContainer.querySelectorAll('.slider-dot'));
    }
    
    // Create arrows if enabled and multiple slides
    if (this.config.showArrows && this.slideCount > 1) {
      // Previous button
      const prevButton = document.createElement('button');
      prevButton.className = 'slider-prev';
      prevButton.setAttribute('aria-label', 'Previous slide');
      prevButton.innerHTML = '❮';
      prevButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.prev();
      });
      
      // Next button
      const nextButton = document.createElement('button');
      nextButton.className = 'slider-next';
      nextButton.setAttribute('aria-label', 'Next slide');
      nextButton.innerHTML = '❯';
      nextButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.next();
      });
      
      this.sliderContainer.appendChild(prevButton);
      this.sliderContainer.appendChild(nextButton);
    }
  }

  /**
   * Add event listeners for user interaction
   */
  addEventListeners() {
    // Pause on hover if enabled
    if (this.config.pauseOnHover) {
      this.sliderContainer.addEventListener('mouseenter', () => this.pause());
      this.sliderContainer.addEventListener('mouseleave', () => {
        if (this.config.autoplay) {
          this.play();
        }
      });
    }
    
    // Touch events for swipe support
    this.sliderContainer.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.sliderContainer.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
    
    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        this.prev();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        this.next();
      }
    });
    
    // Add visibility change handler to pause when tab is not active
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.pause();
      } else if (document.visibilityState === 'visible' && this.config.autoplay) {
        this.play();
      }
    });
  }

  /**
   * Handle swipe gestures
   */
  handleSwipe() {
    const SWIPE_THRESHOLD = 50; // Minimum distance to register as swipe
    
    if (this.touchEndX - this.touchStartX > SWIPE_THRESHOLD) {
      // Swipe right - go to previous slide
      this.prev();
    } else if (this.touchStartX - this.touchEndX > SWIPE_THRESHOLD) {
      // Swipe left - go to next slide
      this.next();
    }
  }

  /**
   * Start autoplay
   */
  play() {
    if (!this.isPlaying) {
      this.isPlaying = true;
      this.interval = setInterval(() => {
        this.next();
      }, this.config.duration);
      console.log('Slider autoplay started');
    }
  }

  /**
   * Pause autoplay
   */
  pause() {
    if (this.isPlaying) {
      this.isPlaying = false;
      clearInterval(this.interval);
      this.interval = null;
      console.log('Slider autoplay paused');
    }
  }

  /**
   * Go to the next slide
   */
  next() {
    const nextSlide = (this.currentSlide + 1) % this.slideCount;
    this.goToSlide(nextSlide);
  }

  /**
   * Go to the previous slide
   */
  prev() {
    const prevSlide = (this.currentSlide - 1 + this.slideCount) % this.slideCount;
    this.goToSlide(prevSlide);
  }

  /**
   * Go to a specific slide
   * @param {number} index - Index of the slide to show
   */
  goToSlide(index) {
    if (index >= 0 && index < this.slideCount && index !== this.currentSlide) {
      // Restart autoplay timer for consistent transitions
      if (this.isPlaying) {
        this.pause();
      }
      
      // Hide current slide
      this.slides[this.currentSlide].style.opacity = '0';
      this.slides[this.currentSlide].style.visibility = 'hidden';
      
      // Show new slide
      this.slides[index].style.opacity = '1';
      this.slides[index].style.visibility = 'visible';
      
      // Update active dot
      if (this.dots) {
        this.dots.forEach((dot, i) => {
          if (i === index) {
            dot.classList.add('active');
          } else {
            dot.classList.remove('active');
          }
        });
      }
      
      // Update current slide index
      this.currentSlide = index;
      
      // Resume autoplay if it was active
      if (this.config.autoplay) {
        this.play();
      }
      
      // Log slide change for debugging
      console.log('Changed to slide', index);
    }
  }
}